import { useId, useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import type { QuizQuestion } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { Button, SelectField, TextField } from '../ui';

const MAX_QUESTIONS = 30;
const MIN_OPTIONS = 2;
const MAX_OPTIONS = 6;

let counter = 0;
function newId(prefix: string): string {
  counter += 1;
  return `${prefix}${Date.now().toString(36)}${counter}`;
}

/** A blank single-answer question with two empty options. */
export function blankQuestion(): QuizQuestion {
  const a = newId('o');
  const b = newId('o');
  return { id: newId('q'), text: '', kind: 'single', options: [{ id: a, text: '' }, { id: b, text: '' }], correct: [a], explanation: '' };
}

/** Problems that stop a quiz being saved, as `{ question index, translated message }`. */
export function quizProblems(questions: readonly QuizQuestion[]): Array<{ index: number; message: string }> {
  const out: Array<{ index: number; message: string }> = [];
  questions.forEach((q, index) => {
    if (!q.text.trim()) out.push({ index, message: t('p3w.quiz.problem.text', { n: index + 1 }) });
    if (q.options.some((o) => !o.text.trim())) out.push({ index, message: t('p3w.quiz.problem.option', { n: index + 1 }) });
    const ids = new Set(q.options.map((o) => o.id));
    const correct = q.correct.filter((c) => ids.has(c));
    if (correct.length === 0) out.push({ index, message: t('p3w.quiz.problem.correct', { n: index + 1 }) });
    if (q.kind === 'single' && correct.length > 1) out.push({ index, message: t('p3w.quiz.problem.single', { n: index + 1 }) });
  });
  return out;
}

export interface QuizGrade {
  correct: number;
  total: number;
  percent: number;
  passed: boolean;
}

/** Grades answers (question id to chosen option ids) against questions that carry `correct`. A question is right only on an exact match. */
export function gradeQuiz(questions: readonly QuizQuestion[], answers: Readonly<Record<string, readonly string[]>>, passMarkPercent: number): QuizGrade {
  let correct = 0;
  for (const q of questions) {
    const given = new Set(answers[q.id] ?? []);
    if (given.size === q.correct.length && q.correct.every((c) => given.has(c))) correct += 1;
  }
  const total = questions.length;
  const percent = total === 0 ? 100 : Math.round((correct / total) * 100);
  return { correct, total, percent, passed: percent >= passMarkPercent };
}

export interface QuizEditorProps {
  value: readonly QuizQuestion[];
  onChange: (next: QuizQuestion[]) => void;
  /** Pass mark used by the preview. */
  passMarkPercent?: number;
}

/** Edit questions: text, single or multiple answer, 2 to 6 options, the correct ones and an explanation, plus a preview. */
export function QuizEditor({ value, onChange, passMarkPercent = 80 }: QuizEditorProps) {
  const [preview, setPreview] = useState(false);
  const problems = quizProblems(value);
  const patch = (i: number, next: QuizQuestion) => onChange(value.map((q, j) => (j === i ? next : q)));
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= value.length) return;
    const copy = [...value];
    const a = copy[i]!;
    copy[i] = copy[j]!;
    copy[j] = a;
    onChange(copy);
  };

  return (
    <section aria-label={t('p3w.quiz.editor')} className="space-y-4">
      {value.length === 0 ? <p className="text-sm text-slate-600">{t('p3w.quiz.none')}</p> : null}
      {value.map((q, i) => (
        <QuestionEditor
          key={q.id}
          q={q}
          index={i}
          last={i === value.length - 1}
          onChange={(next) => patch(i, next)}
          onRemove={() => onChange(value.filter((_, j) => j !== i))}
          onMove={(d) => move(i, d)}
        />
      ))}
      {problems.length > 0 ? (
        <ul role="alert" className="list-disc pl-5 text-sm text-red-700">
          {problems.map((p, k) => (
            <li key={k}>{p.message}</li>
          ))}
        </ul>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button disabled={value.length >= MAX_QUESTIONS} onClick={() => onChange([...value, blankQuestion()])}>
          <Plus size={16} aria-hidden="true" />
          {t('p3w.quiz.addQuestion')}
        </Button>
        <Button disabled={value.length === 0 || problems.length > 0} aria-pressed={preview} onClick={() => setPreview((p) => !p)}>
          {preview ? t('p3w.quiz.hidePreview') : t('p3w.quiz.preview')}
        </Button>
      </div>
      {preview && problems.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <p className="mb-2 text-xs font-medium uppercase text-slate-600">{t('p3w.quiz.previewNote')}</p>
          <QuizRunner questions={value} passMarkPercent={passMarkPercent} preview />
        </div>
      ) : null}
    </section>
  );
}

function QuestionEditor({
  q,
  index,
  last,
  onChange,
  onRemove,
  onMove,
}: {
  q: QuizQuestion;
  index: number;
  last: boolean;
  onChange: (q: QuizQuestion) => void;
  onRemove: () => void;
  onMove: (d: -1 | 1) => void;
}) {
  const setOption = (id: string, text: string) => onChange({ ...q, options: q.options.map((o) => (o.id === id ? { ...o, text } : o)) });
  const toggleCorrect = (id: string, on: boolean) => {
    if (q.kind === 'single') onChange({ ...q, correct: on ? [id] : [] });
    else onChange({ ...q, correct: on ? [...q.correct, id] : q.correct.filter((c) => c !== id) });
  };
  const setKind = (kind: QuizQuestion['kind']) => onChange({ ...q, kind, correct: kind === 'single' ? q.correct.slice(0, 1) : q.correct });
  const removeOption = (id: string) => onChange({ ...q, options: q.options.filter((o) => o.id !== id), correct: q.correct.filter((c) => c !== id) });
  const n = index + 1;
  return (
    <fieldset className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
      <legend className="px-1 text-sm font-semibold">{t('p3w.quiz.questionN', { n })}</legend>
      <TextField label={t('p3w.quiz.questionText')} value={q.text} maxLength={500} onChange={(e) => onChange({ ...q, text: e.target.value })} />
      <SelectField label={t('p3w.quiz.kind')} value={q.kind} onChange={(e) => setKind(e.target.value as QuizQuestion['kind'])}>
        <option value="single">{t('p3w.quiz.kind.single')}</option>
        <option value="multiple">{t('p3w.quiz.kind.multiple')}</option>
      </SelectField>
      <div className="space-y-2">
        <p className="text-sm font-medium text-slate-800">{t('p3w.quiz.options')}</p>
        <p className="text-xs text-slate-600">{t('p3w.quiz.optionsHint')}</p>
        {q.options.map((o, k) => (
          <div key={o.id} className="flex flex-wrap items-end gap-2">
            <label className="flex min-h-10 items-center gap-2 text-sm">
              <input
                type={q.kind === 'single' ? 'radio' : 'checkbox'}
                name={`correct-${q.id}`}
                className="h-5 w-5 accent-[var(--brand-primary)]"
                checked={q.correct.includes(o.id)}
                onChange={(e) => toggleCorrect(o.id, e.target.checked)}
                aria-label={t('p3w.quiz.markCorrect', { n: k + 1, q: n })}
              />
            </label>
            <TextField
              className="min-w-0 flex-1"
              label={t('p3w.quiz.optionN', { n: k + 1 })}
              value={o.text}
              maxLength={200}
              onChange={(e) => setOption(o.id, e.target.value)}
            />
            <Button
              variant="ghost"
              disabled={q.options.length <= MIN_OPTIONS}
              aria-label={t('p3w.quiz.removeOption', { n: k + 1, q: n })}
              onClick={() => removeOption(o.id)}
            >
              <Trash2 size={16} aria-hidden="true" />
            </Button>
          </div>
        ))}
        <Button disabled={q.options.length >= MAX_OPTIONS} onClick={() => onChange({ ...q, options: [...q.options, { id: newId('o'), text: '' }] })}>
          <Plus size={16} aria-hidden="true" />
          {t('p3w.quiz.addOption')}
        </Button>
      </div>
      <TextField label={t('p3w.quiz.explanation')} hint={t('p3w.quiz.explanationHint')} value={q.explanation} maxLength={500} onChange={(e) => onChange({ ...q, explanation: e.target.value })} />
      <div className="flex gap-2">
        <Button variant="ghost" disabled={index === 0} aria-label={t('p3w.quiz.moveUp', { n })} onClick={() => onMove(-1)}>
          <ArrowUp size={16} aria-hidden="true" />
        </Button>
        <Button variant="ghost" disabled={last} aria-label={t('p3w.quiz.moveDown', { n })} onClick={() => onMove(1)}>
          <ArrowDown size={16} aria-hidden="true" />
        </Button>
        <Button variant="ghost" aria-label={t('p3w.quiz.removeQuestion', { n })} onClick={onRemove}>
          <Trash2 size={16} aria-hidden="true" />
          {t('p3w.quiz.removeQuestion.short')}
        </Button>
      </div>
    </fieldset>
  );
}

/** A question as shown to a person: no answers. `correct` and `explanation` appear only when previewing. */
export type RunnerQuestion = Omit<QuizQuestion, 'correct' | 'explanation'> & Partial<Pick<QuizQuestion, 'correct' | 'explanation'>>;

export interface QuizRunnerProps {
  questions: readonly RunnerQuestion[];
  passMarkPercent: number;
  /** Called with question id to chosen option ids. In `preview` the quiz is graded here instead. */
  onSubmit?: (answers: Record<string, string[]>) => void;
  disabled?: boolean;
  /** Preview: needs `correct` on every question; grades locally and shows the explanations. */
  preview?: boolean;
}

/** Take a quiz: radios for single, checkboxes for multiple, every question must be answered before Submit. */
export function QuizRunner({ questions, passMarkPercent, onSubmit, disabled, preview }: QuizRunnerProps) {
  const id = useId();
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [grade, setGrade] = useState<QuizGrade | null>(null);
  const answered = questions.every((q) => (answers[q.id] ?? []).length > 0);

  const choose = (q: RunnerQuestion, optionId: string, on: boolean) => {
    setGrade(null);
    setAnswers((a) => {
      const cur = a[q.id] ?? [];
      const next = q.kind === 'single' ? (on ? [optionId] : []) : on ? [...cur, optionId] : cur.filter((c) => c !== optionId);
      return { ...a, [q.id]: next };
    });
  };

  const submit = () => {
    if (preview) {
      setGrade(gradeQuiz(questions.map((q) => ({ ...q, correct: q.correct ?? [], explanation: q.explanation ?? '' })), answers, passMarkPercent));
      return;
    }
    onSubmit?.(answers);
  };

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (answered && !disabled) submit();
      }}
    >
      {questions.map((q, i) => (
        <fieldset key={q.id} className="space-y-2" disabled={disabled}>
          <legend className="text-sm font-semibold">
            {i + 1}. {q.text}
          </legend>
          <p className="text-xs text-slate-600">{q.kind === 'single' ? t('p3w.quiz.chooseOne') : t('p3w.quiz.chooseAll')}</p>
          {q.options.map((o) => (
            <label key={o.id} className="flex min-h-10 items-center gap-3 text-sm">
              <input
                type={q.kind === 'single' ? 'radio' : 'checkbox'}
                name={`${id}-${q.id}`}
                className="h-5 w-5 accent-[var(--brand-primary)]"
                checked={(answers[q.id] ?? []).includes(o.id)}
                onChange={(e) => choose(q, o.id, e.target.checked)}
              />
              {o.text}
            </label>
          ))}
          {grade && q.explanation ? <p className="text-sm text-slate-700">{q.explanation}</p> : null}
        </fieldset>
      ))}
      {!answered ? <p className="text-xs text-slate-600">{t('p3w.quiz.answerAll')}</p> : null}
      <Button type="submit" variant="primary" disabled={!answered || disabled}>
        {t('p3w.quiz.submit')}
      </Button>
      {grade ? (
        <p role="status" className={`text-sm font-medium ${grade.passed ? 'text-green-800' : 'text-red-700'}`}>
          {t(grade.passed ? 'p3w.quiz.passed' : 'p3w.quiz.failed', { correct: grade.correct, total: grade.total, percent: grade.percent, mark: passMarkPercent })}
        </p>
      ) : null}
    </form>
  );
}

